import { useRules, useSavedRules } from '../api/queries'
import { useTenant } from '../api/tenant'
import { Failure } from '../components/Failure'

/**
 * Rule lifecycle, as a list.
 *
 * The backtest column exists because it is the precondition for enabling a rule, and
 * a screen that hides the precondition makes the refusal look arbitrary.
 */
export function RulesPage({ tenants = [] }: { tenants?: string[] }) {
  const [tenant] = useTenant(tenants)
  const query = useRules(tenant || undefined)
  const authored = useSavedRules(tenant || undefined)

  if (query.isError) return <Failure error={query.error} onRetry={() => query.refetch()} />

  return (
    <section aria-labelledby="rules-heading">
      <h2 id="rules-heading">Rules</h2>
      {query.isSuccess && query.data.length === 0 && (
        <p className="empty">
          No rule has a recorded state yet. Rules with no state still run — a state row is
          how a rule is turned off, not how it is turned on.
        </p>
      )}
      {/* Rules written here, listed apart from the lifecycle table on purpose: the
          question this section answers is "what did we write", and it is a different
          question from "what is running". A saved rule with no backtest appears in
          both — as a rule that exists, and as one that cannot be enabled. */}
      {authored.isSuccess && authored.data.length > 0 && (
        <section aria-labelledby="authored-heading" style={{ marginBottom: 'var(--space-5)' }}>
          <h3 id="authored-heading">Written here</h3>
          <p className="muted">
            Saved from a hunt. A saved rule is a draft: it detects nothing until it has been
            backtested over a real window, enabled, and given to the detector.
          </p>
          <ul style={{ listStyle: 'none', padding: 0 }}>
            {authored.data.map((rule) => (
              <li key={rule.id} className="panel">
                <strong>{rule.title}</strong>
                <p className="mono muted" style={{ margin: 0 }}>
                  {rule.rule_id} · saved by {rule.created_by} on{' '}
                  {new Date(rule.created_at).toLocaleDateString()}
                </p>
                <details>
                  <summary>The rule itself</summary>
                  <pre className="mono" style={{ overflow: 'auto' }}>
                    {rule.sigma}
                  </pre>
                </details>
              </li>
            ))}
          </ul>
        </section>
      )}

      {query.isSuccess && query.data.length > 0 && (
        <table className="panel" style={{ width: '100%', borderCollapse: 'collapse' }}>
          <caption className="muted" style={{ captionSide: 'bottom', textAlign: 'left' }}>
            A rule cannot be enabled without a backtest over at least seven days.
          </caption>
          <thead>
            <tr style={{ textAlign: 'left' }}>
              <th scope="col">Rule</th>
              <th scope="col">Status</th>
              <th scope="col">Backtest</th>
              <th scope="col">Would have fired</th>
            </tr>
          </thead>
          <tbody>
            {query.data.map((rule) => (
              <tr key={rule.id} style={{ borderTop: '1px solid var(--border)' }}>
                <td>{rule.rule_title || rule.rule_id}</td>
                <td>{rule.status}</td>
                <td className="mono">
                  {rule.backtested_at
                    ? `${rule.backtest_window_days} day(s)`
                    : 'never — cannot be enabled'}
                </td>
                <td className="mono">{rule.backtest_matches}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  )
}
